import { useTranslation } from 'react-i18next';
import { DataUnavailable } from '@components/DataUnavailable/DataUnavailable';
import { useSchedulerPanelOpen } from '../../SchedulerPage/hooks/useSchedulerStore';
import type { useActivityFilters } from '../hooks/useActivityFilters';
import { useDeferredReady } from '@hooks/useDeferredReady';
import { useIssueRoute } from '../hooks/useIssueRoute';
import { useOpenTaskPanel } from '../hooks/useActivityPanel';
import type { IListItem, ListItemKind } from '../model/listItem';
import { ViewLoadingCard } from '../ViewLoading/ViewLoadingCard';
import { useWorkPlan } from './useWorkPlan';
import type { IPlanWindow } from './workPlanModel';
import { WorkPlanGrid } from './WorkPlanGrid';
import { WorkPlanSummary } from './WorkPlanSummary';
import { WorkPlanToolbar } from './WorkPlanToolbar';
import styles from './workPlan.module.css';

export interface IWorkPlanProps {
  siteId: string;
  /** The page's unified items, UNFILTERED (`useUnifiedList().allItems`) — the
   *  view narrows them with the panel itself, minus the date scope its window
   *  replaces. */
  items: IListItem[];
  /** The site's today. */
  today: string;
  /** The page's date window (its date picker); null until the site's zone
   *  resolves. */
  window: IPlanWindow | null;
  loading: boolean;
  /** What a failed feed takes from the view (`sourceOutage`): `'all'` stands
   *  the outage in for the grid, one kind leaves the grid with a note naming
   *  what's missing. */
  outage: 'all' | ListItemKind | null;
  /** The page's filters — read, and written by the summary's counts. */
  f: ReturnType<typeof useActivityFilters>;
}

/**
 * The Work Plan: committed activities and issues on a day grid (docs/specs/
 * 2026-09-22-work-plan-view-design.md). The third view of the page — same
 * header, same feed, same drawers: it renders none of its own. A bar opens the
 * page's shared TaskPanel, an issue chip the page's issue drawer.
 *
 * Deferred first paint, like the tracker: the swap commits the loading card,
 * the grid renders at transition priority.
 */
export function WorkPlan(props: IWorkPlanProps) {
  const { t } = useTranslation();
  const ready = useDeferredReady();
  if (props.outage === 'all') return <DataUnavailable className={styles.card} />;
  if (!ready || props.loading || props.window === null) {
    return <ViewLoadingCard label={t('activities.workPlan.loading')} className={styles.card} />;
  }
  return <WorkPlanBody {...props} window={props.window} />;
}

function WorkPlanBody({ siteId, items, today, window, outage, f }: IWorkPlanProps & { window: IPlanWindow }) {
  const { t } = useTranslation();
  const plan = useWorkPlan({ siteId, items, filters: f.filters, today, window });
  const openTask = useOpenTaskPanel();
  const { open: openIssue } = useIssueRoute();
  const selectedTaskId = useSchedulerPanelOpen()?.taskId;

  return (
    <div className={styles.card}>
      <WorkPlanToolbar controls={plan.controls}>
        <WorkPlanSummary summary={plan.summary} f={f} />
      </WorkPlanToolbar>
      {outage && <DataUnavailable compact message={t(`activities.outage.${outage}`)} />}
      <WorkPlanGrid
        window={window}
        groups={plan.groups}
        lane={plan.lane}
        groupBy={plan.controls.groupBy}
        today={today}
        selectedTaskId={selectedTaskId}
        onOpenTask={openTask}
        onOpenIssue={openIssue}
        filtersActive={f.activeCount > 0}
        onClearFilters={f.clear}
      />
    </div>
  );
}
